/**
 * Enhanced createSelector utilities for CadActive application
 * Integrates with standardized selector types and provides runtime validation
 */

import { createSelector } from 'reselect';
import { DirectSelector } from '../types/selectors';

/**
 * Enhanced createSelector wrapper that validates all inputs are functions at runtime
 * Uses standardized DirectSelector type from our type system
 */
export function createValidatedSelector<
  R,
  TInputs extends readonly DirectSelector<unknown>[]
>(
  inputSelectors: TInputs,
  resultFunc: (...args: { [K in keyof TInputs]: ReturnType<TInputs[K]> }) => R
): DirectSelector<R> {
  // Runtime validation to catch non-function selectors
  inputSelectors.forEach((selector, index) => {
    if (typeof selector !== 'function') {
      const selectorInfo = String(selector);
      throw new Error(
        `createValidatedSelector: Input selector at index ${index} is not a function.\n` +
        `Expected: function, Got: ${typeof selector}\n` +
        `Value: ${selectorInfo}\n` +
        'This error prevents runtime crashes in createSelector.\n' +
        'Check that all selectors are properly exported and imported.'
      );
    }
  });

  return createSelector(inputSelectors, resultFunc);
}

/**
 * Type-safe mapStateToProps creator specifically for route components
 * Returns DirectSelector that can be used in route files
 */
export function createMapStateToProps<
  R,
  TInputs extends readonly DirectSelector<unknown>[]
>(
  inputSelectors: TInputs,
  resultFunc: (...args: { [K in keyof TInputs]: ReturnType<TInputs[K]> }) => R
): DirectSelector<R> {
  try {
    return createValidatedSelector(inputSelectors, resultFunc);
  } catch (error) {
    console.error('Route mapStateToProps Error:', error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(
      'Failed to create mapStateToProps. This is likely due to:\n' +
      '1. Missing selector export\n' +
      '2. Incorrect import path\n' +
      '3. Selector factory not properly initialized\n' +
      `Original error: ${errorMessage}`
    );
  }
}
